import { AfterViewChecked, Directive, ElementRef } from '@angular/core';

/**
 * Stamps `data-label` onto every body cell of a table, copied from the matching
 * `<th>`, so `.dl-table--cards` can render each row as a labelled card below
 * 768px (see `src/styles/_table.scss`).
 *
 * Why a directive rather than writing `data-label` into the templates: there are
 * eleven tables across seven pages and roughly sixty cells between them, and a
 * hand-written attribute silently goes stale the moment a column is added or
 * reordered. Reading the *rendered* header also handles columns behind an
 * `*ngIf` — the Usage page has two — which a static attribute list cannot
 * express without duplicating the condition.
 *
 * The trade-off is that the labels are not greppable in the template. Three
 * things keep that honest: a cell may always override by carrying its own
 * `data-label`, opt-out classes are respected, and none of this has any effect
 * above 768px, where the attributes are inert. A bug here shows up as missing
 * labels on a phone, never as a broken desktop table.
 */
@Directive({
  selector: 'table[dlCardTable]'
})
export class CardTableDirective implements AfterViewChecked {

  /** Marks a row as stamped, and against which set of headers. */
  private static readonly STAMP = '__dlCardTableSignature';

  /** Cannot occur in header text, so it cannot make two column sets collide. */
  private static readonly SEPARATOR = '\u0001';

  constructor(private readonly host: ElementRef<HTMLTableElement>) {}

  /**
   * Runs after every change-detection pass, which sounds expensive and is not:
   * once a row is stamped this is one string comparison per row, and these
   * tables page at 100 rows. The signature is what makes a re-stamp happen when
   * the column set actually changes, rather than never or on every pass.
   */
  ngAfterViewChecked(): void {
    const table = this.host.nativeElement;
    const headerRow = table.tHead?.rows[0];

    if (!headerRow) {
      return;
    }

    const headers = Array.from(headerRow.cells).map(th => (th.textContent || '').trim());

    if (!headers.length) {
      return;
    }

    const signature = headers.join(CardTableDirective.SEPARATOR);

    for (const body of Array.from(table.tBodies)) {
      const rows = Array.from(body.rows) as Array<HTMLTableRowElement & Record<string, unknown>>;

      for (const row of rows) {
        if (row[CardTableDirective.STAMP] === signature) {
          continue;
        }

        Array.from(row.cells).forEach((cell, index) => this.label(cell, headers[index]));
        row[CardTableDirective.STAMP] = signature;
      }
    }
  }

  private label(cell: HTMLTableCellElement, header: string | undefined): void {
    // An empty-state row spanning the table is not a field, and its single cell
    // does not line up with any one header.
    if (cell.colSpan > 1) {
      return;
    }

    // The template wins: an explicit data-label, or an opt-out class.
    if (cell.hasAttribute('data-label')
      || cell.classList.contains('dl-cell--bare')
      || cell.classList.contains('dl-cell--title')) {
      return;
    }

    cell.setAttribute('data-label', header ?? '');
  }
}
